import GenericCrudService from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/GenericCrudService";
import Project from "../models/Project";
import ApiResponse from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/core/ApiResponse";
import HttpClient from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/utils/HttpClient";
import AutoCompleteUIDTO from "../dto/AutoCompleteUIDTO";
import GenericResponse from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/core/GenericResponse";
import { EnsolversOpsUser } from "../models/EnsolversOpsUser";
import PagedResult from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/core/PagedResult";
import ApiResponseV2 from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/core/ApiResponseV2";
import { ProjectTimelineEntry } from "../types/ProjectTimelineTypes";
import HttpClientV2 from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/utils/HttpClientV2";

class ProjectService extends GenericCrudService<Project> {
  findProjectNames(): Promise<ApiResponse<AutoCompleteUIDTO[]>> {
    return HttpClient.get(`${this.basePath}/projects-names`, true);
  }

  findProjectNamesByInternal(internal: boolean): Promise<ApiResponse<AutoCompleteUIDTO[]>> {
    return HttpClient.get(`${this.basePath}/projects-names/${internal}`, true);
  }

  findAllProjectsNames(onlyExternal: boolean, assigned: boolean): Promise<ApiResponse<AutoCompleteUIDTO[]>> {
    return HttpClient.get(`${this.basePath}/projects-names-by?onlyExternal=${onlyExternal}&assigned=${assigned}`);
  }

  /**
   * Add a Manager to a Project
   * @param projectExternalId
   * @param userExternalId
   * @throws Error if not succeeded (get the message by using: `error.response.data.message`)
   * @returns `Promise<ApiResponse>`
   *
   * @example
   * ```ts
   * ProjectService.addManagerToProject(projectExternalId, userExternalId)
   *   .then(() => console.log('Manager added'))
   *   .catch(e => console.log(e.response.data.message));
   * ```
   */
  addManagerToProject(projectExternalId: string, userExternalId: string): Promise<ApiResponse<GenericResponse>> {
    return HttpClient.post(`${this.basePath}/manager/${projectExternalId}/${userExternalId}`, {});
  }

  /**
   * Remove a Manager from a Project
   * @param projectExternalId
   * @param userExternalId
   * @throws Error if not succeeded (get the message by using: `error.response.data.message`)
   * @returns `Promise<ApiResponse>`
   *
   * @example
   * ```ts
   * ProjectService.removeManagerFromProject(projectExternalId, userExternalId)
   *   .then(() => console.log('Manager removed'))
   *   .catch(e => console.log(e.response.data.message));
   * ```
   */
  removeManagerFromProject(projectExternalId: string, userExternalId: string): Promise<ApiResponse<GenericResponse>> {
    return HttpClient.delete(`${this.basePath}/manager/${projectExternalId}/${userExternalId}`);
  }

  /**
   * Check if User is Manager from Project
   * @param projectExternalId
   * @param userExternalId
   * @throws Error if user is not Manager
   * @returns `Promise<ApiResponse>`
   *
   * @example
   * ```ts
   * ProjectService.isUserManagerFromProject(projectExternalId, userExternalId)
   *   .then(() => console.log('User is Manager'))
   *   .catch(e => console.log('User is not Manager'));
   * ```
   */
  isUserManagerFromProject(projectExternalId: string, userExternalId: string): Promise<ApiResponse<GenericResponse>> {
    return HttpClient.get(`${this.basePath}/manager/check/${projectExternalId}/${userExternalId}`);
  }

  /**
   * Check if User is Manager from Project
   * @param projectExternalId
   * @param userCoreExternalId
   * @throws Error if user is not Manager
   * @returns `Promise<ApiResponse>`
   *
   * @example
   * ```ts
   * ProjectService.isUserCoreManagerFromProject(projectExternalId, userCoreExternalId)
   *   .then(() => console.log('User is Manager'))
   *   .catch(e => console.log('User is not Manager'));
   * ```
   */
  isUserCoreManagerFromProject(projectExternalId: string, userCoreExternalId: string): Promise<ApiResponse<GenericResponse>> {
    return HttpClient.get(`${this.basePath}/manager/check-core/${projectExternalId}/${userCoreExternalId}`);
  }

  /**
   * Check if User is Manager from Project using the assignationExternalId
   * @param assignationExternalId
   * @param userCoreExternalId
   * @throws Error if user is not Manager
   * @returns `Promise<ApiResponse>`
   *
   * @example
   * ```ts
   * ProjectService.isUserCoreManagerFromProjectByAssignation(assignationExternalId, userCoreExternalId)
   *   .then(() => console.log('User is Manager'))
   *   .catch(e => console.log('User is not Manager'));
   * ```
   */
  isUserCoreManagerFromProjectByAssignation(
    assignationExternalId: string,
    userCoreExternalId: string
  ): Promise<ApiResponse<GenericResponse>> {
    return HttpClient.get(`${this.basePath}/manager/check-core-by-assignation/${assignationExternalId}/${userCoreExternalId}`);
  }

  /**
   * Get a list of Managers from a Project
   * @param projectExternalId
   * @throws Error if not succeeded (get the message by using: `error.response.data.message`)
   * @returns `EnsolversOpsUser` array from `Promise<ApiResponse>`
   *
   * @example
   * ```ts
   * ProjectService.getManagerListFromProject(projectExternalId, userExternalId)
   *   .then((response) => console.log(response.getContent()))
   *   .catch(e => console.log(e.response.data.message));
   * ```
   */
  getManagerListFromProject(projectExternalId: string): Promise<ApiResponse<EnsolversOpsUser[]>> {
    return HttpClient.get(`${this.basePath}/manager/get/${projectExternalId}`);
  }

  getProjectsFromManager(): Promise<ApiResponse<Project[]>> {
    return HttpClient.get(`${this.basePath}/manager/get-projects`);
  }

  findProjectsNamesByUserManagerExternalId(): Promise<ApiResponse<AutoCompleteUIDTO[]>> {
    return HttpClient.get(`${this.basePath}/manager/projects-names`, true);
  }

  getAllProjectsByManagerExternalId(
    page: number,
    pageSize: number,
    context: { [key: string]: string | number } = {},
    archived: boolean,
    filter: string
  ): Promise<ApiResponse<PagedResult<Project>>> {
    return HttpClient.get(
      `${this.getBasePath(
        context
      )}/manager/projects-by-user-id?page=${page}&pageSize=${pageSize}&archived=${archived}&projectType=${filter}`
    );
  }

  findAll(page: number, pageSize: number, archived: boolean, filter: string): Promise<ApiResponse<PagedResult<Project>>> {
    return HttpClient.get(`${this.basePath}/all/filtered?page=${page}&pageSize=${pageSize}&archived=${archived}&projectType=${filter}`);
  }

  getTimeline(externalId: string): Promise<ApiResponseV2<ProjectTimelineEntry[]>> {
    const searchParams = new URLSearchParams();
    searchParams.set("externalId", externalId);
    return HttpClientV2.get(`${this.basePath}/timeline?${searchParams.toString()}`);
  }
}

export default new ProjectService("/ensolvers-ops/projects");
